import { CargoOwnerMenuList, CargoOwnerOption, CargoOwnerValueContainer } from "components/bid/form/config";
import { bidPaymentStatusesEnum, bidPointTypesEnum, invoiceTriggerTypes, paymentPeriodTypes } from "helpers/enums";
import { getInitialStateFromConfig } from "helpers/form";
import { distributionBidCargo, distributionBidPoint } from "helpers/models";
import { requiredField } from "helpers/validators";
import compact from "lodash/compact";
import { selectList as cargoOwnersList } from "sources/books/cargoOwners";
import { list as logistsList } from "sources/books/employees";
import { listForSelect as hiredCarsListForSelect } from "sources/books/hiredCars";
import { listForSelect as hiredDriversListForSelect } from "sources/books/hiredDrivers";
import { listForSelect as hiredTrailersListForSelect } from "sources/books/hiredTrailers";
import { list as legalPersons } from "sources/books/legalPersons";

const positiveExpenseValue = (value) => (Number(value) > 0 ? "" : "Сумма должна быть больше нуля");
const expenseCommentLength = (value = "") =>
    !value || value.length <= 128 ? "" : "Комментарий не должен превышать 128 символов";

const paymentValidationConfig = (paymentProperty) => ({
    planPaymentDate: {
        validators: [
            (value, data) =>
                data[paymentProperty]?.paymentStatus &&
                data[paymentProperty].paymentStatus !== bidPaymentStatusesEnum.NotPaid
                    ? requiredField(value)
                    : "",
        ],
    },
    factPaymentDate: {
        validators: [
            (value, data) =>
                data[paymentProperty]?.paymentStatus === bidPaymentStatusesEnum.Paid ? requiredField(value) : "",
        ],
    },
    remainingPayment: {
        validators: [
            (value, data) =>
                data[paymentProperty]?.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid
                    ? requiredField(value)
                    : "",
        ],
    },
});

const expenseConfig = {
    typeId: {
        label: "Статья затрат",
        inputProps: { name: "typeId" },
        dictionary: "expenseType",
        validators: [requiredField],
        required: true,
        menuPortalTarget: document.body,
    },
    value: {
        label: "Сумма расхода",
        inputProps: { name: "value", type: "number", step: "0.01", pattern: "[0-9]+([.,][0-9]+)?" },
        validators: [positiveExpenseValue],
        required: true,
    },
    comment: {
        label: "Комментарий",
        inputProps: { name: "comment", maxLength: 128 },
        textarea: true,
        validators: [expenseCommentLength],
    },
    id: {},
};

const cargoConfig = {
    name: {
        // field
        label: "Наименование груза",
        inputProps: { name: "name" },
    },
    typeId: {
        // select
        label: "Тип товара",
        inputProps: { name: "typeId" },
        dictionary: "cargoType",
        relatedFields: true,
    },
    comment: {
        // field
        label: "Комментарий",
        inputProps: { name: "comment" },
    },
    packType: {
        // field
        label: "Тип упаковки",
        inputProps: { name: "packType" },
    },
    placesCount: {
        // field
        label: "Количество мест",
        inputProps: { name: "placesCount", type: "number", step: "1", min: "0", max: "32767" },
    },
    weight: {
        // field
        label: "Вес, т",
        inputProps: { name: "weight", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    volume: {
        // field
        label: "Объем, м³",
        inputProps: { name: "volume", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    length: {
        // field
        label: "Длина, м",
        inputProps: { name: "length", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    height: {
        // field
        label: "Высота, м",
        inputProps: { name: "height", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    width: {
        // field
        label: "Ширина, м",
        inputProps: { name: "width", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    extendedProperties: [],
    typeOptions: {
        value: [],
        config: {
            id: {},
            entityOptionId: {},
        },
    },
};

const config = {
    createDocumentAssignment: {
        label: "Создать водителю задачу по сдаче документов",
        inputProps: { name: "createDocumentAssignment" },
        type: "checkbox",
    },
    counterpartyId: {
        // async select
        label: "Контрагент",
        inputProps: { name: "counterpartyId" },
        url: cargoOwnersList,
        filterPropName: ["name", "inn", "kpp"],
        components: {
            Option: CargoOwnerOption,
            ValueContainer: CargoOwnerValueContainer,
            MenuList: CargoOwnerMenuList,
        },
        propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
    },
    contractId: {
        label: "Договор с контрагентом",
        inputProps: { name: "contractId" },
        propName: "name",
    },
    isExpressBid: {
        label: "Экспресс-заявка",
        inputProps: { name: "isExpressBid" },
        type: "checkbox",
    },
    "expressBid.settings": {
        config: {
            averageSpeed: {
                label: "Средняя скорость",
                inputProps: { name: "averageSpeed", type: "number", step: "1" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
            workHours: {
                label: "Кол-во рабочих часов",
                inputProps: { name: "workHours", type: "number", step: "1" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
            dayStart: {
                label: "Начало дня",
                inputProps: { name: "dayStart", type: "time" },
                required: true,
                validators: [(value, data) => (data.isExpressBid ? requiredField(value) : "")],
            },
        },
    },
    expressBid: { value: { settings: {} } },
    legalPersonId: {
        // select
        label: "Организация",
        inputProps: { name: "legalPersonId" },
        propName: "name",
        url: legalPersons,
    },
    responsibleId: {
        label: "Ответственный",
        inputProps: { name: "responsibleId" },
        url: logistsList,
        propName: "fullNameOrEmail",
    },
    price: {
        // field
        label: "Цена",
        inputProps: { name: "price", type: "number", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    priceWithoutVatOnTop: {
        // field
        label: "Цена",
        inputProps: { name: "priceWithoutVatOnTop", type: "number", pattern: "[0-9]+([.,][0-9]+)?" },
    },
    isVatTop: {
        // field
        label: "НДС сверху",
        inputProps: { name: "isVatTop" },
    },
    paymentTypeId: {
        // select
        label: "Тип оплаты",
        inputProps: { name: "paymentTypeId" },
        dictionary: "paymentType",
    },
    ndsTypeId: {
        // select
        label: "НДС",
        inputProps: { name: "ndsTypeId" },
        dictionary: "nDSType",
    },
    paymentPeriodType: {
        // select
        label: "Вид срока оплаты",
        inputProps: { name: "paymentPeriodType" },
        options: paymentPeriodTypes,
    },
    paymentPeriodInDays: {
        label: "Срок оплаты",
        inputProps: { name: "paymentPeriodInDays", type: "number", step: "1" },
    },
    invoiceTriggerType: {
        label: "Основание для выставления счёта",
        inputProps: { name: "invoiceTriggerType" },
        options: invoiceTriggerTypes,
    },
    trailerTypeId: {
        // field
        label: "Тип прицепа",
        inputProps: { name: "trailerTypeId" },
        dictionary: "trailerType",
    },
    hasHiredCar: {
        label: "Наемный ТС",
        inputProps: { name: "hasHiredCar" },
        type: "checkbox",
    },
    isMandatory: {
        label: "Обязательный заказ",
        inputProps: { name: "isMandatory" },
        type: "checkbox",
    },
    isPreBid: {},
    intOptions: {
        label: "Предзаявка",
        inputProps: { name: "intOptions" },
        value: 0,
    },
    hiredCarCounterpartyId: {
        // async select
        label: "Перевозчик",
        inputProps: { name: "hiredCarCounterpartyId" },
        validators: [(value, data) => (data.hasHiredCar ? requiredField(value) : "")],
        url: cargoOwnersList,
        filterPropName: ["name", "inn", "kpp"],
        components: {
            Option: CargoOwnerOption,
            ValueContainer: CargoOwnerValueContainer,
            MenuList: CargoOwnerMenuList,
        },
        propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
    },
    hiredCarPrice: {
        // field
        label: "Сумма для перевозчика",
        inputProps: { name: "hiredCarPrice", type: "number", pattern: "[0-9]+([.,][0-9]+)?" },
        validators: [(value, data) => (data.hasHiredCar && !data.isHiredCarVatTop ? requiredField(value) : "")],
    },
    hiredCarPriceWithoutVatOnTop: {
        // field
        label: "Сумма для перевозчика",
        inputProps: { name: "hiredCarPriceWithoutVatOnTop", type: "number", pattern: "[0-9]+([.,][0-9]+)?" },
        validators: [(value, data) => (data.hasHiredCar && data.isHiredCarVatTop ? requiredField(value) : "")],
    },
    hiredCarCompletedAt: {
        label: "Дата выполнения",
        inputProps: { name: "hiredCarCompletedAt", type: "datetime" },
        wrapper: "col-auto",
        errorWrapper: "col-sm-8 col-md-9 offset-sm-4 offset-md-3",
    },
    hiredCarNdsTypeId: {
        // select
        label: "НДС для перевозчика",
        inputProps: { name: "hiredCarNdsTypeId" },
        dictionary: "nDSType",
    },
    hiredCarPaymentTypeId: {
        // select
        label: "Тип оплаты для перевозчика",
        inputProps: { name: "hiredCarPaymentTypeId" },
        dictionary: "paymentType",
    },
    hiredCarInvoiceTriggerType: {
        label: "Основание для выставления счёта перевозчику",
        inputProps: { name: "hiredCarInvoiceTriggerType" },
        options: invoiceTriggerTypes,
    },
    hiredCarPaymentPeriodType: {
        // select
        label: "Вид срока оплаты перевозчику",
        inputProps: { name: "hiredCarPaymentPeriodType" },
        options: paymentPeriodTypes,
    },
    hiredCarPaymentPeriodInDays: {
        label: "Срок оплаты перевозчику",
        inputProps: { name: "hiredCarPaymentPeriodInDays", type: "number", step: "1" },
    },
    isHiredCarVatTop: {
        // field
        label: "НДС сверху",
        inputProps: { name: "isHiredCarVatTop" },
    },
    hiredCarId: {
        // async select
        label: "Наёмный автомобиль",
        inputProps: { name: "hiredCarId" },
        url: hiredCarsListForSelect,
        propName: "number",
        isClearable: true,
    },
    hiredTrailerId: {
        // async select
        label: "Наёмный прицеп",
        inputProps: { name: "hiredTrailerId" },
        url: hiredTrailersListForSelect,
        propName: "number",
        isClearable: true,
    },
    hiredDriverId: {
        // async select
        label: "Наёмный водитель",
        inputProps: { name: "hiredDriverId" },
        url: hiredDriversListForSelect,
        propName: "fullName",
        isClearable: true,
    },
    cargos: {
        config: cargoConfig,
        value: [distributionBidCargo],
    },
    expenses: {
        config: expenseConfig,
        value: [],
    },
    customerPayment: { config: paymentValidationConfig("customerPayment"), value: null },
    hiredCarPayment: { config: paymentValidationConfig("hiredCarPayment"), value: null },
    typeOptions: {
        value: [],
        config: {
            id: {},
            entityOptionId: {},
        },
    },
    comment: {
        // field
        label: "Комментарий",
        inputProps: { name: "comment" },
    },
    contractNumber: {
        // field
        label: "Номер договора с контрагентом",
        inputProps: { name: "contractNumber" },
    },
    clientBidNumber: {
        // field
        label: "Номер заявки клиента",
        inputProps: { name: "clientBidNumber" },
    },
    clientBidDate: {
        // field
        label: "Дата заявки клиента",
        inputProps: { name: "clientBidDate", type: "date" },
    },
    bidPoints: {
        value: [
            { ...distributionBidPoint, type: bidPointTypesEnum.LoadPoint, order: 0 },
            { ...distributionBidPoint, type: bidPointTypesEnum.UnloadPoint, order: 1 },
        ],
        config: {
            "geozone.address": {
                validators: [requiredField],
            },
            planEnterDate: {
                validators: [requiredField],
            },
            planLeaveDate: {
                validators: [
                    (value, data) => (bidPointTypesEnum.CustomPoint === data.type ? requiredField(value) : ""),
                ],
            },
            counterpartyId: {
                // async select
                inputProps: { name: "counterpartyId" },
                url: cargoOwnersList,
                filterPropName: ["name", "inn", "kpp"],
                components: {
                    Option: CargoOwnerOption,
                    ValueContainer: CargoOwnerValueContainer,
                    MenuList: CargoOwnerMenuList,
                },
                propName: ({ name, inn, kpp }) => (inn || kpp ? `${name} (${compact([inn, kpp]).join("/")})` : name),
            },
            externalCity: {
                // field
                label: "Город для 1С",
                inputProps: { name: "externalCity" },
            },
            comment: {},
            counterpartyPointId: {},
            secondaryPlanEnterDate: {},
            contactPerson: {},
            extendedProperties: [],
        },
    },
    customPointTypeId: {
        validators: [(value, data) => (bidPointTypesEnum.CustomPoint === data.type ? requiredField(value) : "")],
    },
    contactPerson: {
        name: {
            label: "Контактное лицо",
            inputProps: {
                name: "name",
            },
            fieldWrapper: "col-sm-8 col-md-5",
            bWrapper: "row form-group",
        },
        number: {
            label: "Мобильный телефон",
            fieldWrapper: "col-sm-8 col-md-5",
            bWrapper: "row",
            inputProps: {
                name: "phoneNumber",
            },
        },
    },
    extendedProperties: [],
    documents: {
        value: [],
    },
    temperatureRegime: {
        config: {
            temperatureMinimum: {
                label: "Минимальный температурный режим, ℃",
                inputProps: { name: "temperatureMinimum", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
                validators: [
                    (value, data) => (data.temperatureRegime?.temperatureMaximum != null ? requiredField(value) : ""),
                ],
            },
            temperatureMaximum: {
                label: "Максимальный температурный режим, ℃",
                inputProps: { name: "temperatureMaximum", type: "number", step: "0.1", pattern: "[0-9]+([.,][0-9]+)?" },
                validators: [
                    (value, data) => (data.temperatureRegime?.temperatureMinimum != null ? requiredField(value) : ""),
                ],
            },
        },
    },
    organizationId: {}, // readonly attribute
    id: {}, // readonly attribute
    externalId: {}, // readonly attribute
    sourceDistributionBidId: {}, // readonly attribute
};

const initialState = getInitialStateFromConfig(config);

export { config, initialState };
